// File: src/pages/master/LocationPin.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { ImDownload } from "react-icons/im";

import {
  _GetByUrlOnly,
  _Put,
  _DeleteByUrl,
  _PostSave,
} from "../../import/ApiEndPoint/ApiCalling";
import {
  viewpincodedata,
  updatepincodedata,
  deletesinglepincode,
  savepincodedata,
} from "../../import/ApiEndPoint/Api";

/* ------------ tiny inline styles (no scss) ------------ */
const injectOnce = () => {
  if (document.getElementById("lp-inline-css2")) return;
  const s = document.createElement("style");
  s.id = "lp-inline-css2";
  s.textContent = `
    .lp2-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .lp2-toolbar{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .lp2-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .lp2-tablewrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .lp2-table{ width:100%; border-collapse:collapse; min-width:950px; }
    .lp2-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; text-align:left; white-space:nowrap;
    }
    .lp2-row{ border-bottom:1px solid #f1f5f9; }
    .lp2-cell{ padding:8px 10px; font-size:14px; color:#0f172a; vertical-align:middle; }
    .lp2-row:hover{ background:#f9fbff; }
    .lp2-link{ cursor:pointer; color:#111827; font-weight:700; }
    .lp2-actions{ display:flex; gap:8px; align-items:center; }
    .lp2-chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; color:#fff; cursor:pointer; font-weight:800; }
    .lp2-edit{ background:#0ea5e9; }
    .lp2-del{ background:#ef4444; }
    .lp2-muted{ color:#6b7280 }
    .lp2-pager{ display:flex; align-items:center; gap:8px; }
    .lp2-btn{ height:30px; padding:0 10px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; cursor:pointer; }
    .lp2-btn:disabled{ opacity:.5; cursor:not-allowed; }
    .lp2-sel{ height:30px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; padding:0 8px; }
    .lp2-grow{ flex:1 1 240px; }
    .lp2-input{ background:#fff; max-width:160px; }
  `;
  document.head.appendChild(s);
};

/* ------------ helpers ------------ */
const toKey = (r) =>
  [r?.state, r?.district, r?.city, r?.pincode]
    .map((v) =>
      String(v ?? "")
        .trim()
        .toLowerCase()
    )
    .join("|");

const dedup = (arr) => {
  const seen = new Set();
  const out = [];
  for (const r of Array.isArray(arr) ? arr : []) {
    const k = toKey(r);
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(r);
    }
  }
  return out;
};

/* ------------ component ------------ */
export default function LocationPin() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // filters
  const [fState, setFState] = useState("");
  const [fDistrict, setFDistrict] = useState("");
  const [fCityPin, setFCityPin] = useState("");

  // upload
  const [file, setFile] = useState(null);

  // expand controls
  const [openStates, setOpenStates] = useState(() => new Set());
  const [openDistricts, setOpenDistricts] = useState(() => new Set()); // `${state}||${district}`

  // pagination (state level)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => injectOnce(), []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await _GetByUrlOnly(viewpincodedata);
      const list = Array.isArray(res?.pinCodeList) ? res.pinCodeList : [];
      setRows(dedup(list));
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ------------ filtering & grouping ------------ */
  const filtered = useMemo(() => {
    const s = fState.trim().toLowerCase();
    const d = fDistrict.trim().toLowerCase();
    const c = fCityPin.trim().toLowerCase();
    return rows.filter((r) => {
      const hitS =
        !s ||
        String(r.state ?? "")
          .toLowerCase()
          .includes(s);
      const hitD =
        !d ||
        String(r.district ?? "")
          .toLowerCase()
          .includes(d);
      const cityStr = String(r.city ?? "").toLowerCase();
      const pinStr = String(r.pincode ?? "").toLowerCase();
      const hitC = !c || cityStr.includes(c) || pinStr.includes(c);
      return hitS && hitD && hitC;
    });
  }, [rows, fState, fDistrict, fCityPin]);

  // group data
  const grouped = useMemo(() => {
    const byState = new Map();
    for (const r of filtered) {
      const s = r.state || "-";
      const d = r.district || "-";
      if (!byState.has(s)) byState.set(s, new Map());
      const dm = byState.get(s);
      if (!dm.has(d)) dm.set(d, []);
      dm.get(d).push(r);
    }

    // sort
    const states = Array.from(byState.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );
    return states.map(([state, dm]) => ({
      state,
      districts: Array.from(dm.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([district, items]) => ({
          district,
          items: items.slice().sort((a, b) => {
            const c = String(a.city || "").localeCompare(String(b.city || ""));
            return c !== 0
              ? c
              : String(a.pincode || "").localeCompare(String(b.pincode || ""));
          }),
        })),
    }));
  }, [filtered]);

  /* ------------ pagination (states) ------------ */
  const totalPages = Math.max(1, Math.ceil(grouped.length / pageSize));
  const currPage = Math.min(page, totalPages);
  const start = (currPage - 1) * pageSize;
  const pageStates = grouped.slice(start, start + pageSize);
  useEffect(() => setPage(1), [fState, fDistrict, fCityPin, pageSize]);

  /* ------------ expand handlers ------------ */
  const toggleState = (stateName) => {
    setOpenStates((prev) => {
      const next = new Set(prev);
      next.has(stateName) ? next.delete(stateName) : next.add(stateName);
      return next;
    });
  };
  const toggleDistrict = (stateName, district) => {
    const key = `${stateName}||${district}`;
    setOpenDistricts((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  /* ------------ edit/delete leaf rows ------------ */
  const openEdit = (row) => {
    setEditData({ ...row });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editData?._id) return;

    // duplicate guard
    const k = toKey(editData);
    const exists = rows.find(
      (r) => toKey(r) === k && String(r._id) !== String(editData._id)
    );
    if (exists) {
      swal(
        "Duplicate",
        "Same State, District, City, Pincode already exists.",
        "warning"
      );
      return;
    }

    try {
      const res = await _Put(updatepincodedata, editData._id, editData);
      if (res?.status) {
        setEditOpen(false);
        setEditData(null);
        await load();
        swal("Updated", "Location updated.", "success");
      } else {
        swal("Failed", res?.message || "Update failed", "error");
      }
    } catch (e) {
      console.error(e);
      swal("Error", "Could not update", "error");
    }
  };

  const delOne = async (row) => {
    const choice = await swal("Warning", "Delete this record?", {
      buttons: {
        cancel: "Cancel",
        confirm: { text: "Delete", value: "delete" },
      },
      icon: "warning",
      dangerMode: true,
    });
    if (choice !== "delete") return;

    try {
      const res = await _DeleteByUrl(deletesinglepincode, row._id);
      if (res?.status) {
        await load();
        swal("Deleted", "Record removed.", "success");
      } else {
        swal("Failed", res?.message || "Delete failed", "error");
      }
    } catch (e) {
      console.error(e);
      swal("Error", "Could not delete", "error");
    }
  };

  /* ------------ upload (optional duplicate guard) ------------ */
  const onUpload = async () => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);

      // OPTIONAL: prevent client-side dup import by filtering workbook first on the backend.
      // If your backend already dedups, you can just post the file:
      const res = await _PostSave(savepincodedata, fd);
      if (res?.status) {
        swal("Uploaded", "Pin codes uploaded successfully.", "success");
        setFile(null);
        await load();
      } else {
        swal("Failed", res?.message || "Upload failed", "error");
      }
    } catch (e) {
      console.error(e);
      swal("Error", "Could not upload", "error");
    }
  };

  /* ------------ download (full) ------------ */
  const downloadExcelAll = () => {
    const header = ["state", "district", "city", "pincode"];
    const body = rows.map((r) => [
      r?.state || "",
      r?.district || "",
      r?.city || "",
      r?.pincode || "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "location_pins");
    XLSX.writeFile(wb, "LocationPins.xlsx");
  };

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "60vh" }}>
        <Spinner color="primary" style={{ width: 56, height: 56 }}>
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Top toolbar (single row) ===== */}
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="lp2-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div className="lp2-toolbar">
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Location Pins{" "}
                <span className="lp2-pill">{filtered.length}</span>
              </div>

              <div className="lp2-grow" />

              <Input
                className="lp2-input"
                placeholder="State"
                value={fState}
                onChange={(e) => setFState(e.target.value)}
              />
              <Input
                className="lp2-input"
                placeholder="District"
                value={fDistrict}
                onChange={(e) => setFDistrict(e.target.value)}
              />
              <Input
                className="lp2-input"
                placeholder="City / Pincode"
                value={fCityPin}
                onChange={(e) => setFCityPin(e.target.value)}
              />

              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ maxWidth: 220, background: "#fff" }}
              />
              <Button
                color="primary"
                disabled={!file}
                onClick={onUpload}
                title="Upload"
                style={{ height: 36 }}
              >
                Upload
              </Button>

              <Button
                outline
                color="success"
                onClick={downloadExcelAll}
                title="Download Excel"
              >
                <ImDownload size={18} />
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Data card (expand across columns) ===== */}
      <Row>
        <Col sm="12">
          <Card className="lp2-card" style={{ padding: 8 }}>
            {grouped.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center" }}
                className="lp2-muted"
              >
                No records found.
              </div>
            ) : (
              <>
                <div className="lp2-tablewrap">
                  <table className="lp2-table">
                    <thead>
                      <tr>
                        <th style={{ width: "26%" }}>State</th>
                        <th style={{ width: "26%" }}>District</th>
                        <th style={{ width: "26%" }}>City</th>
                        <th style={{ width: "14%" }}>Pincode</th>
                        <th style={{ width: "8%" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageStates.map(({ state, districts }) => {
                        const sOpen = openStates.has(state);

                        // 1) STATE ROW
                        const stateRow = (
                          <tr key={`state-${state}`} className="lp2-row">
                            <td className="lp2-cell">
                              <span
                                className="lp2-link"
                                onClick={() => toggleState(state)}
                              >
                                {sOpen ? "− " : "+ "}
                                {state || "-"}
                              </span>
                            </td>
                            <td className="lp2-cell lp2-muted">—</td>
                            <td className="lp2-cell lp2-muted">—</td>
                            <td className="lp2-cell lp2-muted">—</td>
                            <td className="lp2-cell lp2-muted">—</td>
                          </tr>
                        );

                        // 2) DISTRICT ROWS (only when state is open)
                        const districtRows = !sOpen
                          ? null
                          : districts.map(({ district, items }) => {
                              const key = `${state}||${district}`;
                              const dOpen = openDistricts.has(key);
                              return (
                                <React.Fragment key={`dist-${key}`}>
                                  <tr className="lp2-row">
                                    <td className="lp2-cell lp2-muted"></td>
                                    <td className="lp2-cell">
                                      <span
                                        className="lp2-link"
                                        onClick={() =>
                                          toggleDistrict(state, district)
                                        }
                                      >
                                        {dOpen ? "− " : "+ "}
                                        {district || "-"}
                                      </span>
                                    </td>
                                    <td className="lp2-cell lp2-muted">—</td>
                                    <td className="lp2-cell lp2-muted">—</td>
                                    <td className="lp2-cell lp2-muted">—</td>
                                  </tr>

                                  {/* 3) LEAF ROWS: show in columns 3 & 4 + actions */}
                                  {dOpen &&
                                    items.map((r) => (
                                      <tr className="lp2-row" key={r?._id}>
                                        <td className="lp2-cell lp2-muted"></td>
                                        <td className="lp2-cell lp2-muted"></td>
                                        <td className="lp2-cell">
                                          {r?.city || "-"}
                                        </td>
                                        <td className="lp2-cell">
                                          {r?.pincode || "-"}
                                        </td>
                                        <td className="lp2-cell">
                                          <div className="lp2-actions">
                                            <span
                                              className="lp2-chip lp2-edit"
                                              title="Edit"
                                              onClick={() => openEdit(r)}
                                            >
                                              ✎
                                            </span>
                                            <span
                                              className="lp2-chip lp2-del"
                                              title="Delete"
                                              onClick={() => delOne(r)}
                                            >
                                              ×
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            });

                        return (
                          <React.Fragment key={`state-block-${state}`}>
                            {stateRow}
                            {districtRows}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* bottom pager */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: 8,
                  }}
                >
                  <div className="lp2-pager">
                    <span className="lp2-muted">
                      Page {currPage} / {totalPages}
                    </span>
                    <select
                      className="lp2-sel"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}/page
                        </option>
                      ))}
                    </select>
                    <button
                      className="lp2-btn"
                      onClick={() => setPage(1)}
                      disabled={currPage === 1}
                    >
                      First
                    </button>
                    <button
                      className="lp2-btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currPage === 1}
                    >
                      Prev
                    </button>
                    <button
                      className="lp2-btn"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currPage === totalPages}
                    >
                      Next
                    </button>
                    <button
                      className="lp2-btn"
                      onClick={() => setPage(totalPages)}
                      disabled={currPage === totalPages}
                    >
                      Last
                    </button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Edit Modal ===== */}
      <Modal isOpen={editOpen} toggle={() => setEditOpen(false)}>
        <ModalHeader toggle={() => setEditOpen(false)}>
          Edit Location
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>State</Label>
            <Input
              value={editData?.state || ""}
              onChange={(e) =>
                setEditData((p) => ({ ...p, state: e.target.value }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>District</Label>
            <Input
              value={editData?.district || ""}
              onChange={(e) =>
                setEditData((p) => ({ ...p, district: e.target.value }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>City</Label>
            <Input
              value={editData?.city || ""}
              onChange={(e) =>
                setEditData((p) => ({ ...p, city: e.target.value }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>Pincode</Label>
            <Input
              value={editData?.pincode || ""}
              onChange={(e) =>
                setEditData((p) => ({ ...p, pincode: e.target.value }))
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveEdit}>
            Save
          </Button>
          <Button color="secondary" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
